import { Link } from "react-router-dom";
import { Check, ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { BrandPatternLayer } from "@/components/BrandPatternLayer";
import { site } from "@/lib/siteConfig";
import { pageContent } from "@/cms/cmsModel";
import { useCmsDocument } from "@/cms/CmsContentProvider";

export default function About() {
  useCmsDocument();
  const copy = pageContent.about;
  const paragraphs = copy.paragraphs;
  const services = copy.services;
  const gallery = copy.gallery;
  return (
    <>
      {/* Hero – full-bleed image with a neutral (non-blue) scrim */}
      <section className="relative flex min-h-[440px] items-center overflow-hidden bg-[#0e1216] text-[#F1F4F8] lg:min-h-[520px]">
        <img
          src={copy.hero.image}
          alt={copy.hero.alt}
          loading="eager"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "linear-gradient(100deg, rgba(12,16,20,0.82) 0%, rgba(12,16,20,0.62) 32%, rgba(12,16,20,0.28) 60%, rgba(12,16,20,0.05) 100%)",
          }}
        />
        <div className="relative az-container py-16 lg:py-20">
          <Reveal immediate>
            <span className="az-eyebrow" style={{ color: "#C99A3D" }}>
              <span className="inline-block h-px w-6 bg-current" /> {copy.hero.eyebrow || site.name}
            </span>
          </Reveal>
          <Reveal immediate delay={0.05}>
            <h1 className="mt-4 max-w-3xl font-heading text-4xl font-bold leading-[1.08] tracking-[-0.02em] sm:text-5xl lg:text-6xl">
              {copy.hero.title}
            </h1>
          </Reveal>
          <Reveal immediate delay={0.1}>
            <p className="mt-5 max-w-2xl text-lg text-[rgba(244,246,249,0.9)]">
              {copy.hero.subtitle}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Content */}
      <section className="relative isolate overflow-hidden bg-[#F1F4F8]/90 py-16 lg:py-24">
        <BrandPatternLayer variant="corporate" composition="a" />
        <div className="relative z-10 az-container">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="az-service-frame relative overflow-hidden rounded-3xl">
                  <img
                    src={copy.contentImage.src}
                    alt={copy.contentImage.alt}
                    className="h-[300px] w-full object-cover sm:h-[380px]"
                    loading="lazy"
                  />
                </div>
              </Reveal>

              <div className="mt-10 space-y-6 text-[1.075rem] leading-[1.85] text-[#2f3a49]">
                {paragraphs.map((p, i) => (
                  <Reveal key={i}>
                    <p>{p}</p>
                  </Reveal>
                ))}
              </div>
            </div>

            {/* Service list */}
            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-28">
                <Reveal>
                  <div className="rounded-3xl border border-[#D5DEE8] bg-[#E7EDF5] p-7 shadow-[var(--shadow-soft)]">
                    <h2 className="font-heading text-2xl font-semibold text-[#193D6D]">
                      {copy.servicesHeading}
                    </h2>
                    <div className="mt-6 space-y-3" data-testid="about-services">
                      {services.map((s, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-3 rounded-2xl border border-[#D5DEE8] bg-white p-4"
                          data-testid="about-service"
                        >
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#7A1516]/[0.08] text-[#7A1516]">
                            <Check size={18} />
                          </span>
                          <p className="min-w-0 flex-1 font-medium leading-snug text-[#193D6D]">
                            {s}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </Reveal>
              </div>
            </div>
          </div>

          {/* Gallery – remaining brand images, no repeats */}
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="about-gallery">
            {gallery.map((g, i) => (
              <Reveal
                key={g.src}
                delay={i * 0.06}
                className="az-service-frame relative aspect-square overflow-hidden rounded-3xl shadow-[var(--shadow-card)]"
              >
                <img
                  src={g.src}
                  alt={g.alt}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </Reveal>
            ))}
          </div>

          {/* Closing statement */}
          <Reveal>
            <div className="az-service-frame relative mt-14 overflow-hidden rounded-3xl bg-[#193D6D] p-8 text-center sm:p-12">
              <img
                src={copy.cta.image}
                alt=""
                aria-hidden="true"
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover object-center"
              />
              <div className="relative">
                <span className="az-eyebrow justify-center" style={{ color: "#C99A3D" }}>
                  <span className="inline-block h-px w-6 bg-current" /> {copy.cta.eyebrow}
                </span>
                <p className="mx-auto mt-5 max-w-3xl font-heading text-2xl leading-snug text-[#F1F4F8] sm:text-3xl">
                  {copy.cta.text}{" "}
                  <span className="text-[#C99A3D]">{copy.cta.highlight}</span>
                </p>
                <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                  <Link to="/kontakt">
                    <Button className="rounded-xl bg-[#7A1516] px-6 py-6 text-base text-[#F1F4F8] hover:bg-[#941d1e]">
                      {copy.cta.contactButton} <ArrowRight size={18} className="ml-2" />
                    </Button>
                  </Link>
                  <a href={`tel:${site.primaryPhoneTel}`}>
                    <Button
                      variant="outline"
                      className="rounded-xl border-[rgba(244,246,249,0.35)] bg-transparent px-6 py-6 text-base text-[#F1F4F8] hover:bg-[rgba(244,246,249,0.08)]"
                    >
                      <Phone size={18} className="mr-2" /> {copy.cta.phoneButton}
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
